/**
 * SafetyRouteController orchestrates the whole safety-routing flow and is the only safety module that
 * touches the map, the layers and the DOM-driven controls. It is created by Map.create() after the
 * DirectionsControl exists, and exposed so MapController (the AI-assistant facade) can trigger it too.
 *
 * Flow of planSafetyRoutes():
 *   1. Read the filter config (SafetyFilterPanel) and the already-loaded PSR/incident features
 *      (layer.markerStorage — populated on map load regardless of layer visibility).
 *   2. Request the standard route (Google alternatives included) and score every alternative.
 *   3. Select secure-parking waypoints along the best standard route and request a waypoint-injected
 *      "safety" route; optionally add a best-effort incident-avoidance nudge.
 *   4. Keep the safety route only if it scores better than the standard one; otherwise fall back to the
 *      safest of Google's own alternatives.
 *   5. Draw the routes (RouteRenderManager) and render the comparison cards (RouteComparisonView).
 *
 * See the module-level note in IncidentAvoidance.js: incident *avoidance* is best-effort with Google.
 */

import axios from "axios";
import { RouteRenderManager } from "./RouteRenderManager";
import { RouteComparisonView } from "./RouteComparisonView";
import { SafetyFilterPanel } from "./SafetyFilterPanel";
import { scoreRoute, routeTotals } from "./RouteScorer";
import { selectWaypoints, MAX_WAYPOINTS } from "./PsrWaypointSelector";
import { filterIncidents, incidentPenalty, computeNudgeWaypoints } from "./IncidentAvoidance";
import { densifyPath } from "./GeoUtils";

/* eslint-disable no-undef */

const COLOR_STANDARD = "#00ffee";
const COLOR_SAFETY = "#28a745";
const COLOR_ALTERNATIVE = "#ffc107";

export class SafetyRouteController {
  /**
   * @param {import("../Map").Map} map
   */
  constructor(map) {
    this.map = map;
    this.directionsControl = map.directionsControl;
    this.filterPanel = new SafetyFilterPanel(document);
    this.renderManager = new RouteRenderManager(map.map, window.directionsRenderer);
    this.comparison = new RouteComparisonView(document.getElementById("safetyRouteComparison"));
    this.options = [];
    this.activeIndex = 0;
    this.scoreSequence = 0;

    const planButton = document.getElementById("planSafetyRoute");
    if (planButton) {
      planButton.addEventListener("click", (event) => {
        event.preventDefault();
        this.planSafetyRoutes();
      });
    }

    // When the master toggle is on, plan a safety route right after a normal search. The standard
    // search draws first; planSafetyRoutes() then re-requests and takes over rendering.
    const routingForm = document.querySelector("#routingForm");
    if (routingForm) {
      routingForm.addEventListener("submit", () => {
        if (this.filterPanel.getConfig().enabled) {
          setTimeout(() => this.planSafetyRoutes(), 0);
        }
      });
    }
  }

  _features(layerKey) {
    const layer = this.map.dataLayers ? this.map.dataLayers[layerKey] : null;
    return layer && Array.isArray(layer.markerStorage) ? layer.markerStorage : [];
  }

  /**
   * Plans and displays the standard + safety route(s).
   *
   * @param {{origin?: string, destination?: string}} [opts]
   * @returns {Promise<boolean>}
   */
  async planSafetyRoutes(opts = {}) {
    const origin = opts.origin || document.getElementById("startingPoint").value;
    const destination = opts.destination || document.getElementById("destination").value;
    if (!origin || !destination) {
      return false;
    }
    if (opts.origin) {
      document.getElementById("startingPoint").value = origin;
    }
    if (opts.destination) {
      document.getElementById("destination").value = destination;
    }

    const config = this.filterPanel.getConfig();
    const psrFeatures = this._features("parkingSpots");
    const incidentFeatures = this._features("incidents");
    const nowSeconds = Math.floor(Date.now() / 1000);

    if (!psrFeatures.length) {
      this.comparison.message("Secure parking data is still loading — please try again in a moment.");
      return false;
    }

    // Surface the relevant layers so the user can see what the route is being scored against.
    this._setLayer("parkingSpots", true);
    if (config.avoidIncidents) {
      this._setLayer("incidents", true);
    }

    this.comparison.message("Calculating resilience-optimized routes…");

    let standardResponse;
    try {
      standardResponse = await this.directionsControl.requestRoutePromise(this._request(origin, destination));
    } catch (status) {
      this.comparison.message(`No route found (${status}). Please enter more specific locations.`);
      return false;
    }

    const baselineRoute = standardResponse.routes[0];
    const baselineDistance = routeTotals(baselineRoute).distance;
    const scoreCtx = { psrFeatures, incidentFeatures, config, baselineDistance, nowSeconds };

    const standardScore = scoreRoute(baselineRoute, scoreCtx);
    this._lastStandardScore = standardScore;
    this._lastConfig = config;
    const options = [
      { response: standardResponse, routeIndex: 0, color: COLOR_STANDARD, label: "Standard route", score: standardScore, isBaseline: true },
    ];

    // Try an active safety route via secure-parking waypoint injection. We show it whenever one can be
    // built (i.e. there is at least one secure parking in the corridor) and let the user decide whether
    // the detour is worth it — that is the whole point of the comparison. If none can be built, fall
    // back to the safest of Google's own alternatives.
    const safetyOption = await this._buildSafetyRoute(origin, destination, standardScore, scoreCtx);
    if (safetyOption) {
      options.push(safetyOption);
    } else {
      const altOption = this._bestAlternative(standardResponse, standardScore, scoreCtx);
      if (altOption) {
        options.push(altOption);
      }
    }

    // Diagnostics: makes it obvious in the console when the *data* (not the routing) is the limiting
    // factor — e.g. PSR locations that sit nowhere near the route.
    console.info(
      `[SafetyRouting] PSR loaded: ${psrFeatures.length}, in corridor (≤${config.maxCorridorDistance / 1000} km): ${standardScore.psrCount}, safety route: ${safetyOption ? "yes" : "no"}`
    );

    this.options = options;
    this.activeIndex = 0;
    this.renderManager.render(options, 0);
    this._renderComparison(0);
    this._scoreOptions(options);
    return true;
  }

  /**
   * Canonical server score per candidate (concept §11/§14): every route variant is sent to
   * POST routing/assess with quick=true (no evidence tables) using the same inputs as the main
   * assessment, so the comparison cards show the engine's 1–100 score instead of a second,
   * client-side number. Skipped silently when the endpoint is not available (no Resilience flag).
   *
   * @param {Array<object>} options
   */
  _scoreOptions(options) {
    const rac = this.map.riskAssessmentController;
    const assessUrl = this.map.apiUrls && this.map.apiUrls.assess;
    if (!assessUrl || !rac || typeof rac.buildPayload !== "function") {
      return;
    }
    const sequence = ++this.scoreSequence;
    const headers = {
      "X-Requested-With": "XMLHttpRequest",
      "Content-Type": "application/json",
      "X-CSRF-Token": this.map.apiUrls.csrfToken || "",
    };
    options.forEach((option) => {
      option.serverScore = { pending: true };
    });
    this._renderComparison(this.activeIndex);

    const requests = options.map((option) => {
      const route = option.response && option.response.routes ? option.response.routes[option.routeIndex] : null;
      // buildPayload encodes the detailed step path; no polyline => the route has no usable geometry.
      const payload = route ? rac.buildPayload(route) : null;
      if (!payload || !payload.polyline) {
        option.serverScore = { error: true };
        return Promise.resolve();
      }
      payload.quick = true;
      return axios
        .post(assessUrl, payload, { headers, timeout: 120000 })
        .then((response) => {
          const data = response && response.data;
          if (sequence !== this.scoreSequence) {
            return;
          }
          if (data && data.ok !== false && data.kpi && data.kpi.overallScore) {
            option.serverScore = {
              value: data.kpi.overallScore.value,
              band: data.kpi.overallScore.band,
              confidence: data.kpi.overallScore.confidence,
              assessmentId: data.assessmentId,
              bands: data.bands,
            };
          } else {
            option.serverScore = { error: true };
          }
        })
        .catch(() => {
          if (sequence === this.scoreSequence) {
            option.serverScore = { error: true };
          }
        });
    });
    Promise.all(requests).then(() => {
      if (sequence === this.scoreSequence && this.options === options) {
        this._renderComparison(this.activeIndex);
      }
    });
  }

  /**
   * Builds the waypoint-injected safety route (with an optional incident nudge). Returns an option
   * object or null if no waypoints could be selected / the request failed.
   */
  async _buildSafetyRoute(origin, destination, standardScore, scoreCtx) {
    const { config, nowSeconds, incidentFeatures } = scoreCtx;
    const totalLength = standardScore.distance;
    const filteredIncidents = config.avoidIncidents ? filterIncidents(incidentFeatures, config, nowSeconds) : [];

    const { waypoints } = selectWaypoints(
      standardScore.psrEntries,
      totalLength,
      filteredIncidents,
      config,
      nowSeconds
    );
    if (!waypoints.length) {
      return null;
    }

    let safetyResponse;
    try {
      safetyResponse = await this.directionsControl.requestRoutePromise(
        this._request(origin, destination, waypoints)
      );
    } catch (status) {
      return null;
    }
    let safetyScore = scoreRoute(safetyResponse.routes[0], scoreCtx);

    // Best-effort incident nudge: if we are still passing a cluster, try to steer around it.
    if (config.avoidIncidents && safetyScore.incidentNearCount > 0 && waypoints.length < MAX_WAYPOINTS) {
      const densePath = densifyPath(safetyResponse.routes[0].overview_path, 1000);
      const { near } = incidentPenalty(densePath, filteredIncidents, config, nowSeconds);
      const nudges = computeNudgeWaypoints(densePath, near, config, 1);
      if (nudges.length) {
        try {
          const nudged = await this.directionsControl.requestRoutePromise(
            this._request(origin, destination, [...waypoints, ...nudges])
          );
          const nudgedScore = scoreRoute(nudged.routes[0], scoreCtx);
          // Keep the nudge only if it actually reduces the incident penalty without a big detour.
          if (nudgedScore.incidentPenalty < safetyScore.incidentPenalty && nudgedScore.detourRatio < 0.4) {
            safetyResponse = nudged;
            safetyScore = nudgedScore;
          }
        } catch (status) {
          /* keep the un-nudged safety route */
        }
      }
    }

    return {
      response: safetyResponse,
      routeIndex: 0,
      color: COLOR_SAFETY,
      label: "Resilience route",
      score: safetyScore,
      isBaseline: false,
    };
  }

  /**
   * Scores Google's own alternative routes (routes[1..]) and returns the safest one if it beats the
   * standard route, else null.
   */
  _bestAlternative(response, standardScore, scoreCtx) {
    let best = null;
    for (let i = 1; i < response.routes.length; i++) {
      const score = scoreRoute(response.routes[i], scoreCtx);
      if (score.safety > standardScore.safety && (!best || score.safety > best.score.safety)) {
        best = { response, routeIndex: i, color: COLOR_ALTERNATIVE, label: "More resilient alternative", score, isBaseline: false };
      }
    }
    return best;
  }

  _renderComparison(activeIndex) {
    this.comparison.render(this.options, activeIndex, (i) => this._onSelect(i));
    if (this.options.length <= 1) {
      const score = this._lastStandardScore;
      const km = this._lastConfig ? this._lastConfig.maxCorridorDistance / 1000 : "?";
      const message =
        score && score.psrCount === 0
          ? `No secure parking lies within ${km} km of this route. Increase the corridor distance, or check that PSR locations exist near this route.`
          : "No more resilient route variant could be generated for this route with the current filters.";
      this.comparison.appendNote(message, "alert alert-warning mt-2 small mb-0");
      return;
    }
    this.comparison.appendNote(
      "Incident avoidance is best-effort: routes are scored and steered, not guaranteed to skip an area.",
      "text-muted small mt-2 mb-0"
    );
  }

  _onSelect(index) {
    this.activeIndex = index;
    this.renderManager.setActive(index);
    this._renderComparison(index);
  }

  /**
   * Clears all safety routes and restores the standard renderer appearance.
   * @returns {boolean}
   */
  clear() {
    this.scoreSequence++;
    this.renderManager.clear();
    this.comparison.clear();
    this.options = [];
    this.activeIndex = 0;
    return true;
  }

  _request(origin, destination, waypoints) {
    return {
      origin,
      destination,
      unitSystem: google.maps.UnitSystem.METRIC,
      travelMode: google.maps.DirectionsTravelMode.DRIVING,
      provideRouteAlternatives: true,
      waypoints: (waypoints || []).map((location) => ({ location, stopover: false })),
    };
  }

  _setLayer(key, on) {
    const layer = this.map.dataLayers ? this.map.dataLayers[key] : null;
    if (layer && typeof layer.enable === "function") {
      if (on && typeof layer.isEnabled === "function" && !layer.isEnabled()) {
        layer.enable();
      }
    }
  }

  /**
   * Snapshot for the assistant facade.
   * @returns {object}
   */
  getState() {
    return {
      active: this.options.length > 0,
      routes: this.options.map((o) => ({
        label: o.label,
        distance: o.score.distance,
        duration: o.score.duration,
        psrCount: o.score.psrCount,
        incidentNearCount: o.score.incidentNearCount,
        coverage: o.score.coverageScore,
        safety: o.score.safety,
        riskScore: o.serverScore && Number.isFinite(Number(o.serverScore.value)) ? o.serverScore.value : null,
        riskBand: o.serverScore && o.serverScore.band ? o.serverScore.band : null,
      })),
    };
  }
}
